import React, { ReactElement, useMemo } from 'react';
import { GoogleAdsLocationGq } from '../../../graphql/projects/models/GoogleAdsLocationGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import VoxelAutocomplete, {
  GenericOption,
  VoxelAutocompleteProps,
} from '../../voxel/Forms/VoxelAutocomplete';
import VoxelText from '../../voxel/Text/VoxelText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-light-svg-icons';
import VoxelAutocomplete2 from '../../voxel/Forms/VoxelAutocomplete';
import { getEmojiFlag } from '../../../logic/otherUtilities/getEmojiFlag';

export interface VoxelAutocompleteCountryProps
  extends Omit<
    VoxelAutocompleteProps,
    | 'setSelectedOptions'
    | 'options'
    | 'selectedOptionValues'
    | 'fixedOptions'
    | 'freeAdd'
    | 'multi'
    | 'emptyAllowed'
    | 'placeholder'
    | 'label'
    | 'infoText'
  > {
  selectedCountry?: GoogleAdsLocationGq;
  setSelectedCountry: (value?: GoogleAdsLocationGq) => void;
  clearSelectedCity?: () => void;
  type: 'keyword' | 'rank' | 'both' | 'user';
  isRequired?: boolean;
}
export const globalOption: GoogleAdsLocationGq = {
  id: 1,
  name: 'Global',
  canonicalName: 'dummy',
  targetType: 'dummy',
  countryCode: 'dummy',
};

export const VoxelAutocompleteCountry: React.FC<VoxelAutocompleteCountryProps> = ({
  labelPosition,
  selectedCountry,
  setSelectedCountry,
  clearSelectedCity,
  type,
  hasLock: editDisabledText,
  isRequired: requiredLabel,
  ...forwardedProps
}) => {
  const countryQuery = projectsApiService.queries.googleAds.useGetLocations({
    targetType: 'Country',
  });

  const locationList = useMemo(() => countryQuery?.result ?? [], [countryQuery.result]);

  const locationOptions: GenericOption[] = useMemo(
    () =>
      locationList.map((location) => ({
        value: location.id,
        title: getEmojiFlag(location.countryCode) + ' ' + location.name,
      })),
    [locationList]
  );

  const optionsWithGlobal: GenericOption[] = useMemo(
    () => [
      {
        value: globalOption.id,
        title: globalOption.name,
        icon: <FontAwesomeIcon icon={faGlobe} size="lg" className="-ml-0.5 text-voxel-gray-600" />,
      },
      ...locationOptions,
    ],
    [locationOptions]
  );

  const selectedOptionValues: number[] = useMemo(
    () => (selectedCountry?.id ? [selectedCountry?.id] : [globalOption.id]),
    [selectedCountry?.id]
  );

  const changeCountry = (option: GenericOption) => {
    const test = locationList?.find((location) => location?.id === option?.value);
    if (test === undefined && (type === 'keyword' || type === 'both')) {
      setSelectedCountry(globalOption);
    } else setSelectedCountry(test);
    clearSelectedCity != undefined && clearSelectedCity();
  };

  const infoText: ReactElement = (
    <VoxelText div className="w-64 p-2 text-[13px]">
      Google search behaviour is tied to a specific location based on a variety of signals,
      including users' settings, devices, and behavior on the platform.
      <br></br>
      <br></br>
      By targeting a specific location you will recieve the aggregated search behaviour data from
      that area. If location is not set, search behaviour from all locations will be included.
      <br></br>
    </VoxelText>
  );
  return (
    <VoxelAutocomplete2
      {...forwardedProps}
      label={`${type === 'rank' ? 'Rank Country' : 'Country'}`}
      labelPosition={labelPosition ?? 'left'}
      isRequired={requiredLabel}
      placeholder={'N/A'}
      options={type === 'rank' || type === 'user' ? locationOptions : optionsWithGlobal}
      selectedOptionValues={selectedOptionValues}
      setSelectedOptions={(options) => changeCountry(options[0])}
      emptyAllowed={false}
      infoText={type === 'keyword' && labelPosition === 'top' ? infoText : null}
      hasLock={editDisabledText}
    />
  );
};

export default VoxelAutocompleteCountry;
